import { genericError } from "@/models/GenericError";
import { KsKey, KsKeyWrapper } from "@/models/KsKey";
import { ListQuery } from "@/models/ListQuery";
import { Name } from "@/models/Name";
import { PaginationModel } from "@/models/Pagination";
import { DeleteEntityResult } from "@/models/services/DeleteEntityResult";
import { Deserializer } from "jsonapi-serializer";
import { apiConnector } from "./presetService";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { StoreEntityResult } from "@/models/services/StoreEntityResult";
import { NO_CONTENT } from "@/constants/response-status-codes";
import { convertTextPwdToBase64Pwd } from "@/utilities/Validation";


export const getKsKeys = async (listParameters?: ListQuery): Promise<KsKeyWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const userLoginFields = "fields[userLogin]=master";
        const masterFields = "fields[master]=mstDisplayname";
        const ksKeyTypeFields = "fields[ksKeyType]=trnName";
        const ksKeyFields = "fields[ksKey]=kskName,userLogin,ksKeyType";
        const url = `ksKey?${partialURL}&include=userLogin.master,ksKeyType&${ksKeyTypeFields}&${userLoginFields}&${masterFields}&${ksKeyFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: KsKey[] = data.map((ksKey): KsKey => {
                    return {
                        id: ksKey['id'],
                        name: ksKey['ksk-name'],
                        userLoginId: ksKey['user-login']['id'],
                        userLoginName: ksKey['user-login']['master'] ? ksKey['user-login']['master']['mst-displayname'] : "-",
                        ksKeyTypeId: ksKey['ks-key-type']['id'],
                        ksKeyTypeName: ksKey['ks-key-type']['trn-name']
                    }
                })

                return resolve({ rows, pagination });
            })

        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const deleteKsKey = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `ksKey/${id}`;
        const ksKey = {
            data: {
                type: "ksKey",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, ksKey);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getKskeyWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const ksKeyFields = "fields[ksKey]=kskName";
        const url = `ksKey?${paginationString}&${ksKeyFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, ksKeys: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const ksKeyNames: Name[] = ksKeys.map((ksKey) => {
                    return {
                        value: ksKey['id'],
                        label: ksKey['ksk-name'],
                    }
                });

                return resolve({ rows: ksKeyNames, pagination: pagination });
            });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getKskeyTypeWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const ksKeyTypeFields = "fields[ksKeyType]=trnName";
        const url = `ksKeyType?${paginationString}&${ksKeyTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, ksKeyTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const ksKeyTypeNames: Name[] = ksKeyTypes.map((ksKeyType) => {
                    return {
                        value: ksKeyType['id'],
                        label: ksKeyType['trn-name'],
                    }
                });

                return resolve({ rows: ksKeyTypeNames, pagination: pagination });
            });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const saveKsKey = async (ksKey: KsKey, key: string): Promise<StoreEntityResult> => {
    try {
        const record = {
            type: "saveKsKey",
            attributes: {
                kskName: ksKey.name,
                ksKeyTypeId: ksKey.ksKeyTypeId,
                key: key
            }
        }

        let response: any = await apiConnector.sendData('', true, record, true);

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    const content = await response.json();
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) ?? genericError });
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const exportKsKey = async (ids: string, password: string): Promise<{ id: string, key: string }[]> => {
    try {
        const headers = {
            type: "exportKsKey",
            ids: ids,
            password: convertTextPwdToBase64Pwd(password)
        }

        const response = await apiConnector.fetchData("", true, headers);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            const keys = Object.keys(json).map(key => {
                return {
                    id: key,
                    key: json[key]
                }
            });

            return resolve(keys);
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}